import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, type ViewInstance, type TextInputInstance } from 'react-native';

import { Portal } from '@/components/ui';
import { useAccounts } from '@/hooks/use-accounting';
import { useDebounced } from '@/hooks/use-debounced';
import { dataService } from '@/services/data-service';
import type { Account } from '@/types/models';
import { type AccountMatch, type AccountSearchResult, mergeAccountMatches } from '@/utils/account-search-rank';
import { formatMoneyCents } from '@/utils/format-money';
import { formatOwnerNames } from '@/utils/owner-name-label';
import { mailingBlockLines } from '@/utils/postal-address';

export interface AccountTypeaheadProps {
	/** Scope the search to one association. Required so the typeahead doesn't
	 * leak accounts across orgs and so account-number prefix matches stay
	 * unambiguous. Pass undefined while the parent is still resolving the
	 * association — the input renders disabled. */
	associationId: string | undefined;
	/** The currently selected account (null = no selection). */
	value: Account | null;
	/** Called when the user picks (or clears) an account. */
	onSelect: (account: Account | null) => void;
	/** Free-text in the input. Optional — caller can pass `undefined` and let
	 * the component manage its own text state. */
	value_text?: string;
	onChangeText?: (text: string) => void;
	/** Called with `true` after the user picks via Tab — the conventional
	 * "advance focus to next field" cue for accounting-style rapid entry.
	 * Caller is responsible for moving focus (e.g. nextRef.current.focus()). */
	onTabAdvance?: () => void;
	placeholder?: string;
	autoFocus?: boolean;
	testID?: string;
	inputRef?: React.RefObject<TextInputInstance | null>;
	disabled?: boolean;
	/** Restrict the candidate pool (e.g. 'active' so staff can't match a
	 * closed/suspended account). Omitted = every status, matching the
	 * historical default for existing callers. */
	status?: string;
	/** Show each row's balance on the right. Defaults on — every accounting
	 * caller wants it; turn off where the balance is noise. */
	showAccountBalance?: boolean;
}

/** Search is a network round-trip; one per pause, not per keystroke. */
const SEARCH_DEBOUNCE_MS = 150;

/**
 * Account autocomplete tuned for keyboard-driven rapid entry.
 *
 * Matching is the work-order Job Location's (#582): the global-search endpoint,
 * scoped to this association, so owner names, mailing addresses and external
 * names all match — and the owner name is SHOWN, so a hit never looks random.
 * Balances come from the accounting account list instead, because search is not
 * flag-gated and deliberately returns none (see api/global-search.ts).
 *
 * Behaviours:
 *   - Account-number prefix matches lead, computed locally so typing "748"
 *     unambiguously narrows the list before search answers.
 *   - ArrowUp/ArrowDown moves the highlight.
 *   - Tab autocompletes to the highlighted match AND fires `onTabAdvance`
 *     so the caller can move focus onward.
 *   - Enter is intentionally NOT bound — Enter belongs to whatever submit
 *     handler the caller wires up at form level.
 *   - Dropdown renders through a Portal at fixed viewport coords (per the
 *     project-wide typeahead z-index rule); no parent stacking context can
 *     paint over it.
 */
export const AccountTypeahead: React.FC<AccountTypeaheadProps> = ({
	associationId,
	value,
	onSelect,
	value_text,
	onChangeText,
	onTabAdvance,
	placeholder = 'Account # or address…',
	autoFocus,
	testID,
	inputRef,
	disabled,
	status,
	showAccountBalance = true,
}) => {
	const isControlled = value_text !== undefined;
	const [internalText, setInternalText] = useState(value?.accountNumber ?? '');
	const text = isControlled ? (value_text as string) : internalText;
	const setText = useCallback((t: string) => {
		if (!isControlled) setInternalText(t);
		onChangeText?.(t);
	}, [isControlled, onChangeText]);

	const [highlight, setHighlight] = useState(0);
	const [open, setOpen] = useState(false);
	const wrapRef = useRef<ViewInstance>(null);
	const ownInputRef = useRef<TextInputInstance>(null);
	const effectiveInputRef = inputRef ?? ownInputRef;
	const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);

	const { data: accountsResp } = useAccounts(
		associationId ? { associationId, limit: 500, status } : { limit: 0 },
	);
	const accounts = useMemo<Account[]>(
		() => (accountsResp?.data ?? []) as Account[],
		[accountsResp],
	);

	const searchText = useDebounced(text.trim(), SEARCH_DEBOUNCE_MS);
	// Tagged with its query so the merge can refuse hits for text no longer in the
	// field. No keepPreviousData: stale hits are exactly what must not be selectable.
	const { data: search } = useQuery<AccountSearchResult>({
		queryKey: ['account-typeahead-search', associationId, searchText],
		queryFn: async () => ({
			query: searchText,
			hits: await dataService.searchAccounts(searchText, { associationId }),
		}),
		enabled: open && !!associationId && searchText.length >= 2,
		staleTime: 30_000,
	});

	const matches = useMemo<Array<AccountMatch<Account>>>(
		() => mergeAccountMatches(accounts, search, text),
		[accounts, search, text],
	);

	useEffect(() => { setHighlight(0); }, [text]);

	// Eagerly select on exact account-number match — the UX expectation is
	// that typing the full number commits to the row without an extra Tab.
	useEffect(() => {
		const q = text.trim().toLowerCase();
		if (!q) {
			if (value) onSelect(null);
			return;
		}
		const exact = accounts.find((a) => (a.accountNumber ?? '').toLowerCase() === q);
		if (exact && exact.id !== value?.id) onSelect(exact);
	}, [text, accounts, value, onSelect]);

	// If the parent updates `value` (e.g. preselected via URL param), reflect
	// that in the displayed text when uncontrolled.
	useEffect(() => {
		if (isControlled) return;
		if (value && value.accountNumber && value.accountNumber !== internalText) {
			setInternalText(value.accountNumber);
		}
	}, [value, isControlled, internalText]);

	const measureAnchor = useCallback(() => {
		const node = wrapRef.current as unknown as {
			getBoundingClientRect?: () => DOMRect;
			measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
		} | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => setAnchorRect({ top: y + h, left: x, width: w }));
		}
	}, []);

	useEffect(() => {
		if (!open) return;
		measureAnchor();
		if (Platform.OS !== 'web') return;
		const onMove = () => measureAnchor();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [open, measureAnchor, matches.length]);

	/** Who a row is, for humans: the owners on file, else the QuickBooks-era external name. */
	const nameOf = (m: AccountMatch<Account>): string =>
		formatOwnerNames(m.ownerNames) || m.account.externalName?.trim() || '';

	const formatSelected = (m: AccountMatch<Account>): string => {
		const num = m.account.accountNumber ?? '';
		const name = nameOf(m);
		return name ? `${num} — ${name}` : num;
	};

	const commit = (m: AccountMatch<Account>, advance: boolean) => {
		setText(formatSelected(m));
		onSelect(m.account);
		setOpen(false);
		if (advance) onTabAdvance?.();
	};

	return (
		<View ref={wrapRef} style={styles.wrap}>
			<TextInput
				ref={effectiveInputRef}
				value={text}
				onChangeText={(v) => { setText(v); setOpen(true); }}
				autoFocus={autoFocus}
				autoCapitalize="characters"
				autoCorrect={false}
				editable={!disabled}
				placeholder={placeholder}
				placeholderTextColor="#90a4ae"
				style={[styles.input, disabled && styles.inputDisabled]}
				onFocus={() => setOpen(true)}
				onBlur={() => setTimeout(() => setOpen(false), 120)}
				testID={testID}
				onKeyPress={(e: any) => {
					const key = e.nativeEvent?.key as string | undefined;
					if (key === 'ArrowDown') {
						e.preventDefault?.();
						setHighlight((h) => Math.min(matches.length - 1, h + 1));
					} else if (key === 'ArrowUp') {
						e.preventDefault?.();
						setHighlight((h) => Math.max(0, h - 1));
					} else if (key === 'Tab') {
						// Commit the highlighted match but let the browser advance
						// focus naturally (no preventDefault) — that way the next
						// form field receives focus without callers needing to
						// wire `onTabAdvance` to a manual `.focus()` call.
						const top = matches[highlight] ?? matches[0];
						if (top) {
							commit(top, true);
						}
					} else if (key === 'Escape') {
						setOpen(false);
					}
				}}
			/>
			{open && matches.length > 0 && anchorRect && (
				<Portal>
					<View
						testID={testID ? `${testID}-dropdown` : 'account-typeahead-dropdown'}
						style={{
							// `fixed` (web) / absolute (native) escapes every parent
							// stacking context — see components/ui/portal.tsx for why.
							position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
							top: anchorRect.top + 4,
							left: anchorRect.left,
							width: anchorRect.width,
							zIndex: 99999,
							maxHeight: 320,
						}}
					>
						<View style={styles.dropdown}>
							<ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="always" nestedScrollEnabled>
								{matches.map((m, idx) => {
									const a = m.account;
									const name = nameOf(m);
									// Search matches the mailing address too; a row found that way
									// has to show it, or it contains none of the words typed (#549).
									const mailing = mailingBlockLines(a);
									return (
										<Pressable
											key={a.id}
											onPress={() => commit(m, true)}
											style={[styles.option, idx === highlight && styles.optionActive]}
										>
											<Text style={styles.optPrimary}>{a.accountNumber ?? a.id.slice(0, 8)}</Text>
											{/* Name over lot, as on the work-order Job Location (#582). */}
											<View style={styles.optBody}>
												{name ? <Text style={styles.optName} numberOfLines={1}>{name}</Text> : null}
												<Text style={styles.optSecondary} numberOfLines={1}>{a.address ?? ''}</Text>
												{mailing.length > 0 ? (
													<Text style={styles.optSecondary} numberOfLines={1}>
														Mail: {mailing.join(', ')}
													</Text>
												) : null}
											</View>
											{showAccountBalance ? (
												<Text style={[
													styles.optBalance,
													(a.balanceCents ?? 0) > 0 && styles.optBalanceOwed,
													(a.balanceCents ?? 0) < 0 && styles.optBalanceCredit,
												]}>
													{formatMoneyCents(a.balanceCents)}
												</Text>
											) : null}
										</Pressable>
									);
								})}
							</ScrollView>
						</View>
					</View>
				</Portal>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	wrap: { position: 'relative' },
	input: {
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		padding: 10, fontSize: 14, backgroundColor: '#fff',
		fontFamily: 'monospace',
	},
	inputDisabled: { backgroundColor: '#f5f5f5', color: '#9e9e9e' },
	dropdown: {
		backgroundColor: '#fff',
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 6 },
		elevation: 24,
		overflow: 'hidden',
	},
	option: {
		flexDirection: 'row', alignItems: 'center', gap: 10,
		paddingVertical: 8, paddingHorizontal: 10,
		borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#eee',
	},
	optionActive: { backgroundColor: '#e3f2fd' },
	optPrimary: { fontFamily: 'monospace', fontWeight: '600', color: '#1a237e', minWidth: 80 },
	// minWidth 0 so numberOfLines can truncate inside a flex row on RN-Web.
	optBody: { flex: 1, minWidth: 0 },
	optName: { color: '#263238', fontSize: 13, fontWeight: '600' },
	optSecondary: { color: '#546e7a', fontSize: 12 },
	optBalance: { color: '#37474f', fontSize: 12, fontFamily: 'monospace', minWidth: 80, textAlign: 'right' },
	optBalanceOwed: { color: '#c62828' },
	optBalanceCredit: { color: '#2e7d32' },
});
